// GE-CAPPS — « Mes Apps » : les apps custom de l'utilisateur (créées via le
// connecteur MCP Claude) + le catalogue public (trame Recette : publish /
// install-fork — le fork copie la SPEC, jamais les données, et s'exécute sur
// les données de l'installeur).
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import {
  makeGetAllRequest,
  makeGetRequest,
  makePostRequest,
  makeDeleteRequest,
} from 'utils/api'
import { appIcon } from './runnerShared'
import './CustomApps.css'

function AppCard({ app, t, onOpen, onPublish, onUnpublish, onDelete }) {
  return (
    <div
      className="ca-card ca-app-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
    >
      <div className="ca-app-head">
        <div className="ca-app-icon" style={{ background: app.color || '#6fa0ff' }}>
          {appIcon(app.icon)}
        </div>
        <div style={{ minWidth: 0 }}>
          <h2 className="ca-app-name">{app.name}</h2>
          <div className="ca-app-meta">
            <span className="ca-mono">/apps/{app.slug}</span>
          </div>
        </div>
      </div>
      <p className="ca-app-desc">{app.description || '\u00A0'}</p>
      <div className="ca-app-meta">
        {app.is_published && (
          <span className="ca-badge is-published">
            {t('customapps.published', 'Publiée')}
            {app.install_count > 0 && ` · ${app.install_count}`}
          </span>
        )}
        {app.catalog_source && (
          <span className="ca-badge is-fork">
            {t('customapps.installed', 'Installée du catalogue')}
          </span>
        )}
        <span>
          {t('customapps.updated', 'Modifiée le')}{' '}
          {new Date(app.updated_at).toLocaleDateString('fr-FR')}
        </span>
      </div>
      <div className="ca-app-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="ca-btn is-primary" onClick={onOpen}>
          {t('customapps.open', 'Ouvrir')}
        </button>
        {app.is_published ? (
          <button type="button" className="ca-btn" onClick={onUnpublish}>
            {t('customapps.unpublish', 'Dépublier')}
          </button>
        ) : (
          <button type="button" className="ca-btn" onClick={onPublish}>
            {t('customapps.publish', 'Publier')}
          </button>
        )}
        <button type="button" className="ca-btn is-danger" onClick={onDelete}>
          {t('customapps.delete', 'Supprimer')}
        </button>
      </div>
    </div>
  )
}

function CatalogCard({ app, t, installing, onInstall }) {
  return (
    <div className="ca-card ca-app-card" style={{ cursor: 'default' }}>
      <div className="ca-app-head">
        <div className="ca-app-icon" style={{ background: app.color || '#6fa0ff' }}>
          {appIcon(app.icon)}
        </div>
        <div style={{ minWidth: 0 }}>
          <h2 className="ca-app-name">{app.name}</h2>
          <div className="ca-app-meta">
            {t('customapps.by', 'par')} {app.author}
          </div>
        </div>
      </div>
      <p className="ca-app-desc">{app.description || '\u00A0'}</p>
      <div className="ca-app-meta">
        <span className="ca-badge">
          {app.views_count} {t('customapps.views', 'vues')}
        </span>
        {(app.sources || []).map((s) => (
          <span key={s} className="ca-badge ca-mono">
            {s}
          </span>
        ))}
        <span>
          {app.install_count} {t('customapps.installs', 'installations')}
        </span>
      </div>
      <div className="ca-app-actions">
        <button
          type="button"
          className="ca-btn is-primary"
          disabled={installing}
          onClick={onInstall}
        >
          {installing
            ? t('customapps.installing', 'Installation…')
            : t('customapps.install', 'Installer')}
        </button>
      </div>
    </div>
  )
}

export function MyApps() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tab, setTab] = useState('mine') // mine | catalog
  const [apps, setApps] = useState([])
  const [catalog, setCatalog] = useState([])
  const [loading, setLoading] = useState(true)
  const [installing, setInstalling] = useState(null)

  const loadMine = useCallback(async () => {
    const resp = await makeGetAllRequest('/api/customapps/apps/', {}, true)
    if (resp?.status === 200) {
      setApps(
        Array.isArray(resp.data) ? resp.data : resp.data?.results || [],
      )
    }
  }, [])

  const loadCatalog = useCallback(async () => {
    const resp = await makeGetRequest('/api/customapps/catalog/', {}, true)
    if (resp?.status === 200) setCatalog(resp.data || [])
  }, [])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      await Promise.all([loadMine(), loadCatalog()])
      setLoading(false)
    }
    load()
  }, [loadMine, loadCatalog])

  const publish = async (app, on) => {
    const resp = await makePostRequest(
      `/api/customapps/apps/${app.slug}/${on ? 'publish' : 'unpublish'}/`,
      {},
      {},
      true,
    )
    if (resp?.status === 200) {
      toast.success(
        on
          ? t('customapps.publishedToast', 'App publiée au catalogue.')
          : t('customapps.unpublishedToast', 'App retirée du catalogue.'),
      )
      await Promise.all([loadMine(), loadCatalog()])
    } else {
      toast.error(resp?.data?.detail || t('customapps.actionError', 'Action impossible.'))
    }
  }

  const remove = async (app) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('customapps.confirmDelete', `Supprimer « ${app.name} » ?`))) {
      return
    }
    const resp = await makeDeleteRequest(`/api/customapps/apps/${app.slug}/`, {}, true)
    if (resp?.status === 204 || resp?.status === 200) {
      toast.success(t('customapps.deletedToast', 'App supprimée.'))
      await loadMine()
    } else {
      toast.error(resp?.data?.detail || t('customapps.actionError', 'Action impossible.'))
    }
  }

  const install = async (app) => {
    setInstalling(app.id)
    const resp = await makePostRequest(
      `/api/customapps/catalog/${app.id}/install/`,
      {},
      {},
      true,
    )
    setInstalling(null)
    if (resp?.status === 201) {
      toast.success(t('customapps.installedToast', 'App installée dans Mes Apps.'))
      await Promise.all([loadMine(), loadCatalog()])
      navigate(`/apps/${resp.data.slug}`)
    } else {
      toast.error(resp?.data?.detail || t('customapps.actionError', 'Action impossible.'))
    }
  }

  return (
    <div className="ca">
      <div className="ca-main">
        <div className="ca-header">
          <div>
            <h1>{t('customapps.title', 'Mes Apps')}</h1>
            <p className="ca-subtitle">
              {t(
                'customapps.subtitle',
                'Vos applications personnelles, construites avec Claude sur vos données Life.',
              )}
            </p>
          </div>
        </div>

        <div className="ca-tabs">
          <button
            type="button"
            className={`ca-tab${tab === 'mine' ? ' is-active' : ''}`}
            onClick={() => setTab('mine')}
          >
            {t('customapps.tabMine', 'Mes Apps')}
            {apps.length > 0 && ` (${apps.length})`}
          </button>
          <button
            type="button"
            className={`ca-tab${tab === 'catalog' ? ' is-active' : ''}`}
            onClick={() => setTab('catalog')}
          >
            {t('customapps.tabCatalog', 'Catalogue')}
            {catalog.length > 0 && ` (${catalog.length})`}
          </button>
        </div>

        {loading && (
          <div className="ca-loading">
            <span className="ca-spinner" />
            {t('customapps.loadingList', 'Chargement…')}
          </div>
        )}

        {!loading && tab === 'mine' && apps.length === 0 && (
          <div className="ca-card ca-empty">
            <h2>{t('customapps.emptyTitle', 'Aucune app pour le moment')}</h2>
            <p>
              {t(
                'customapps.emptyBody',
                'Décrivez l’app dont vous rêvez à Claude (connecteur Life MCP) : il construit la spec, la teste sur vos données et elle apparaît ici. Vous pouvez aussi installer une app du catalogue.',
              )}
            </p>
            <span className="ca-hint">
              {t(
                'customapps.emptyHint',
                '« Claude, crée-moi un dashboard de mes dépenses Courses par mois »',
              )}
            </span>
          </div>
        )}

        {!loading && tab === 'mine' && apps.length > 0 && (
          <div className="ca-grid">
            {apps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                t={t}
                onOpen={() => navigate(`/apps/${app.slug}`)}
                onPublish={() => publish(app, true)}
                onUnpublish={() => publish(app, false)}
                onDelete={() => remove(app)}
              />
            ))}
          </div>
        )}

        {!loading && tab === 'catalog' && catalog.length === 0 && (
          <div className="ca-card ca-empty">
            <h2>{t('customapps.catalogEmptyTitle', 'Le catalogue est vide')}</h2>
            <p>
              {t(
                'customapps.catalogEmptyBody',
                'Personne n’a encore publié d’app. Publiez la vôtre : seule la spec est partagée, jamais vos données.',
              )}
            </p>
          </div>
        )}

        {!loading && tab === 'catalog' && catalog.length > 0 && (
          <div className="ca-grid">
            {catalog.map((app) => (
              <CatalogCard
                key={app.id}
                app={app}
                t={t}
                installing={installing === app.id}
                onInstall={() => install(app)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyApps
